import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  DndContext, DragOverlay, MouseSensor, TouchSensor, pointerWithin, useSensor, useSensors,
  type DragEndEvent, type DragStartEvent,
} from '@dnd-kit/core';
import { AlertCircle, CalendarDays, ChevronLeft, ChevronRight, Lock, Map as MapIcon } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { JobQuickViewSheet } from '@/components/jobs/JobQuickViewSheet';
import { DayTimeline, HOUR_PX } from '@/components/schedule/DayTimeline';
import { WeekGrid } from '@/components/schedule/WeekGrid';
import { ScheduleMap } from '@/components/schedule/ScheduleMap';
import { ScheduleJobCard } from '@/components/schedule/ScheduleJobCard';
import { MoveConfirmDialog } from '@/components/schedule/MoveConfirmDialog';
import { ToBookPanel } from '@/components/schedule/ToBookPanel';
import { EstimateQuickViewSheet } from '@/components/estimates/EstimateQuickViewSheet';
import {
  DEFAULT_WINDOW_MIN, SNAP_MIN, UNASSIGNED,
  fromMinutes, jobDayKey, jobWindow, moveChangesAnything, parseDropId, shiftDay, techColour,
  toMinutes, visibleHours, weekDays, weekStart,
  type ScheduleJob, type ScheduleLane, type ScheduleMove,
} from '@/components/schedule/scheduleModel';
import { useScheduleJobs, useScheduleMove, useToBook } from '@/hooks/useSchedule';
import { useTechsWithMetros, useMetros } from '@/hooks/useReferenceData';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useUserRole } from '@/hooks/useUserRole';
import { useFleetView } from '@/hooks/useFleetView';
import { useMapAreas } from '@/hooks/useMapAreas';
import { deriveTechProgress, isDeclinedByTech } from '@/utils/techProgress';
import { formatInTz } from '@/utils/timezone';
import { cn } from '@/lib/utils';

type View = 'day' | 'week';
const UNASSIGNED_COLOUR = 'hsl(var(--muted-foreground))';

const isDayKey = (v: string | null): v is string => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v);

function pointerX(event: DragEndEvent): number | null {
  const e = event.activatorEvent as MouseEvent | TouchEvent | null;
  if (!e) return null;
  const x = 'touches' in e ? e.touches[0]?.clientX ?? e.changedTouches[0]?.clientX : e.clientX;
  return x == null ? null : x + event.delta.x;
}

export default function Schedule() {
  const { locale, timezone } = useCompanyLocale();
  const [params, setParams] = useSearchParams();
  const today = formatInTz(new Date(), timezone, 'yyyy-MM-dd');
  const view: View = params.get('view') === 'week' ? 'week' : 'day';
  const day = isDayKey(params.get('date')) ? (params.get('date') as string) : today;

  const [metroId, setMetroId] = useState('all');
  const [showMap, setShowMap] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [activeJob, setActiveJob] = useState<ScheduleJob | null>(null);
  const [pendingMove, setPendingMove] = useState<ScheduleMove | null>(null);
  const [selectedEstimateId, setSelectedEstimateId] = useState<string | null>(null);

  const setNav = (next: { view?: View; date?: string }) => {
    const p = new URLSearchParams(params);
    if (next.view) p.set('view', next.view);
    if (next.date) p.set('date', next.date);
    setParams(p, { replace: true });
  };

  const days = useMemo(
    () => (view === 'week' ? weekDays(weekStart(day, locale.weekStartsOn)) : [day]),
    [view, day, locale.weekStartsOn],
  );
  const { data: jobs = [], isLoading, error, refetch } = useScheduleJobs(days[0], days[days.length - 1], timezone);
  const { data: techs = [], isLoading: techsLoading } = useTechsWithMetros(true);
  const { data: metros = [] } = useMetros(true);
  const move = useScheduleMove();
  const { data: toBook } = useToBook();
  const { isAdmin, isCSR } = useUserRole();
  const { fallback: mapFallback } = useMapAreas();

  // The red "now" line, refreshed every minute while today is on screen.
  const [nowMinutes, setNowMinutes] = useState<number | null>(null);
  useEffect(() => {
    if (view !== 'day' || day !== today) { setNowMinutes(null); return; }
    const tick = () => setNowMinutes(toMinutes(formatInTz(new Date(), timezone, 'HH:mm')));
    tick();
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, [view, day, today, timezone]);

  const laneKeyOf = useCallback((job: ScheduleJob) => job.assigned_tech_id ?? UNASSIGNED, []);

  // Jobs in another area are hidden by the area filter; jobs with no area always show.
  const visibleJobs = useMemo(
    () => (metroId === 'all' ? jobs : jobs.filter((j) => !j.metro_id || j.metro_id === metroId)),
    [jobs, metroId],
  );

  const lanes = useMemo<ScheduleLane[]>(() => {
    // Colour by position in the full active list, so a filter never repaints a technician.
    const colourIndex = new Map(techs.map((t, i) => [t.id, i]));
    const eligible = metroId === 'all'
      ? techs
      : techs.filter((t) => !t.tech_metros?.length || t.tech_metros.some((m) => m.metro_id === metroId));
    const result: ScheduleLane[] = [
      { key: UNASSIGNED, techId: null, name: 'Unassigned', colour: UNASSIGNED_COLOUR },
      ...eligible.map((t) => ({ key: t.id, techId: t.id, name: t.name, colour: techColour(colourIndex.get(t.id) ?? 0) })),
    ];
    // A job can sit with a deactivated tech, or one outside the area: give it a lane anyway.
    const seen = new Set(result.map((l) => l.key));
    let extra = techs.length;
    for (const job of visibleJobs) {
      const id = job.assigned_tech_id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const active = colourIndex.has(id);
      result.push({
        key: id,
        techId: id,
        name: job.techs?.name ?? 'Unknown technician',
        colour: techColour(active ? (colourIndex.get(id) as number) : extra++),
        inactive: !active,
      });
    }
    return result;
  }, [techs, metroId, visibleJobs]);

  const colourOf = useCallback(
    (techId: string | null) => (techId ? lanes.find((l) => l.techId === techId)?.colour : undefined) ?? UNASSIGNED_COLOUR,
    [lanes],
  );

  const techName = useCallback(
    (id: string | null) => (id ? lanes.find((l) => l.techId === id)?.name ?? 'Technician' : 'Unassigned'),
    [lanes],
  );

  const dayJobs = useMemo(
    () => visibleJobs.filter((j) => jobDayKey(j, timezone) === day),
    [visibleJobs, timezone, day],
  );

  // Live technician positions on today's map (admins and CSRs; get_fleet_live refuses others).
  // Status is derived from all of today's jobs, so the area filter never hides a tech's own work.
  const showFleet = view === 'day' && showMap && day === today && (isAdmin || isCSR);
  const allTodayJobs = useMemo(
    () => (showFleet ? jobs.filter((j) => jobDayKey(j, timezone) === day) : []),
    [showFleet, jobs, timezone, day],
  );
  const { entries: fleetTechs } = useFleetView({ jobs: allTodayJobs, colourOf, enabled: showFleet });

  const jobsByLane = useMemo(() => {
    const map = new Map<string, ScheduleJob[]>();
    for (const job of dayJobs) {
      const key = laneKeyOf(job);
      map.set(key, [...(map.get(key) ?? []), job]);
    }
    return map;
  }, [dayJobs, laneKeyOf]);

  const weekCells = useMemo(() => {
    const cells = new Map<string, Map<string, ScheduleJob[]>>();
    for (const job of visibleJobs) {
      const d = jobDayKey(job, timezone);
      if (!d) continue;
      const key = laneKeyOf(job);
      const row = cells.get(key) ?? new Map<string, ScheduleJob[]>();
      row.set(d, [...(row.get(d) ?? []), job]);
      cells.set(key, row);
    }
    return cells;
  }, [visibleJobs, timezone, laneKeyOf]);

  const hours = useMemo(() => visibleHours(dayJobs), [dayJobs]);
  const rangeJobs = view === 'day' ? dayJobs : visibleJobs;
  const summary = useMemo(() => {
    const open = rangeJobs.filter((j) => j.status !== 'completed');
    return {
      total: rangeJobs.length,
      unassigned: open.filter((j) => !j.assigned_tech_id).length,
      declined: open.filter((j) => isDeclinedByTech(j)).length,
      notAccepted: open.filter((j) => {
        const stage = deriveTechProgress(j)?.stage;
        return stage === 'not_accepted' || stage === 'rescheduled';
      }).length,
    };
  }, [rangeJobs]);

  // ── Drag and drop ────────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveJob((event.active.data.current?.job as ScheduleJob | undefined) ?? null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const job = event.active.data.current?.job as ScheduleJob | undefined;
    setActiveJob(null);
    if (!job || !event.over) return;
    const target = parseDropId(String(event.over.id));
    if (!target) return;

    const techId = target.laneKey === UNASSIGNED ? null : target.laneKey;
    const w = jobWindow(job);
    let next: ScheduleMove;

    if (target.view === 'week') {
      // Another day or technician; the time of day stays.
      next = {
        job, day: target.day, techId,
        start: w ? fromMinutes(w.start) : null,
        end: w?.hasEnd ? fromMinutes(w.end) : null,
      };
    } else if (target.zone === 'anytime') {
      next = { job, day, techId, start: null, end: null };
    } else {
      // A timed card keeps its grab point (its left edge is its start); an anytime chip
      // starts where the pointer lets go.
      const translated = event.active.rect.current.translated;
      const x = w && translated ? translated.left - 2 : pointerX(event);
      if (x == null) return;
      const duration = w ? w.end - w.start : DEFAULT_WINDOW_MIN;
      const raw = hours.from * 60 + ((x - event.over.rect.left) / HOUR_PX) * 60;
      const start = Math.max(0, Math.min(24 * 60 - duration, Math.round(raw / SNAP_MIN) * SNAP_MIN));
      next = {
        job, day, techId,
        start: fromMinutes(start),
        // A job with no end keeps none; a job placed from "anytime" gets a real window.
        end: !w || w.hasEnd ? fromMinutes(Math.min(start + duration, 23 * 60 + 59)) : null,
      };
    }

    if (moveChangesAnything(next, timezone)) setPendingMove(next);
  };

  const confirmMove = () => {
    if (!pendingMove) return;
    move.mutate(
      {
        jobId: pendingMove.job.id,
        jobNumber: pendingMove.job.job_number,
        day: pendingMove.day,
        start: pendingMove.start,
        end: pendingMove.end,
        techId: pendingMove.techId,
        techName: pendingMove.techId ? techName(pendingMove.techId) : null,
      },
      { onSettled: () => setPendingMove(null) },
    );
  };

  const activeLaneColour = activeJob
    ? lanes.find((l) => l.key === laneKeyOf(activeJob))?.colour ?? UNASSIGNED_COLOUR
    : UNASSIGNED_COLOUR;

  // ── Header labels ────────────────────────────────────────────────────────
  const title = view === 'day'
    ? format(parseISO(day), 'EEEE d MMMM yyyy')
    : `${format(parseISO(days[0]), 'd MMM')} – ${format(parseISO(days[6]), 'd MMM yyyy')}`;
  const step = view === 'day' ? 1 : 7;
  const mapVisible = view === 'day' && showMap;

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold">Schedule</h1>
          <p className="text-muted-foreground">
            Who is where, and when. Drag a job to another technician or time to move it.
          </p>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" aria-label="Previous" onClick={() => setNav({ date: shiftDay(day, -step) })}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={() => setNav({ date: today })} disabled={view === 'day' ? day === today : days.includes(today)}>
              Today
            </Button>
            <Button variant="outline" size="icon" aria-label="Next" onClick={() => setNav({ date: shiftDay(day, step) })}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" className="gap-2 px-2 text-base font-semibold">
                <CalendarDays className="h-4 w-4" />
                {title}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={parseISO(day)}
                weekStartsOn={locale.weekStartsOn}
                onSelect={(d) => d && setNav({ date: format(d, 'yyyy-MM-dd') })}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {metros.length > 1 && (
              <Select value={metroId} onValueChange={setMetroId}>
                <SelectTrigger className="w-[170px]">
                  <SelectValue placeholder="All areas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All areas</SelectItem>
                  {metros.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <ToggleGroup
              type="single"
              variant="outline"
              value={view}
              onValueChange={(v) => v && setNav({ view: v as View })}
            >
              <ToggleGroupItem value="day" className="px-3">Day</ToggleGroupItem>
              <ToggleGroupItem value="week" className="px-3">Week</ToggleGroupItem>
            </ToggleGroup>
            {view === 'day' && (
              <Button
                variant={showMap ? 'secondary' : 'outline'}
                onClick={() => setShowMap((s) => !s)}
                aria-pressed={showMap}
              >
                <MapIcon className="mr-2 h-4 w-4" />
                Map
              </Button>
            )}
          </div>
        </div>

        {/* Summary + legend */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="font-medium">{summary.total} job{summary.total === 1 ? '' : 's'}</span>
          <span className={cn(summary.unassigned > 0 ? 'text-warning' : 'text-muted-foreground')}>
            {summary.unassigned} unassigned
          </span>
          <span className={cn(summary.notAccepted > 0 ? 'text-warning' : 'text-muted-foreground')}>
            {summary.notAccepted} not accepted
          </span>
          <span className={cn(summary.declined > 0 ? 'text-destructive' : 'text-muted-foreground')}>
            {summary.declined} declined
          </span>
          <span className="ml-auto flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <LegendDot className="bg-success" label="Accepted" />
            <LegendDot className="bg-warning" label="Awaiting tech" />
            <LegendDot className="bg-info" label="En route / on site" />
            <LegendDot className="bg-destructive" label="Declined" />
            <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> Can't move</span>
          </span>
        </div>

        {error ? (
          <div className="rounded-lg border py-12 text-center">
            <AlertCircle className="mx-auto mb-3 h-8 w-8 text-destructive" />
            <p className="mb-4 text-muted-foreground">{(error as Error).message}</p>
            <Button variant="outline" onClick={() => refetch()}>Try again</Button>
          </div>
        ) : isLoading || techsLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveJob(null)}
          >
            <div className="mb-4">
              <ToBookPanel
                toBook={toBook}
                timezone={timezone}
                colourOf={colourOf}
                onOpenJob={setSelectedJobId}
                onOpenEstimate={setSelectedEstimateId}
              />
            </div>
            <div className={cn(mapVisible && 'grid gap-4 xl:grid-cols-[minmax(0,1fr)_400px]')}>
              <div className="min-w-0">
                {view === 'day' ? (
                  <DayTimeline
                    lanes={lanes}
                    jobsByLane={jobsByLane}
                    hours={hours}
                    nowMinutes={nowMinutes}
                    timezone={timezone}
                    dragging={!!activeJob}
                    selectedJobId={selectedJobId}
                    onOpenJob={setSelectedJobId}
                  />
                ) : (
                  <WeekGrid
                    lanes={lanes}
                    days={days}
                    cells={weekCells}
                    today={today}
                    timezone={timezone}
                    dragging={!!activeJob}
                    selectedJobId={selectedJobId}
                    onOpenJob={setSelectedJobId}
                    onOpenDay={(d) => setNav({ view: 'day', date: d })}
                  />
                )}
                {techs.length === 0 && (
                  <p className="mt-3 text-sm text-muted-foreground">
                    No active technicians yet. Add them in Settings to give each one a lane.
                  </p>
                )}
              </div>
              {mapVisible && (
                <div className="h-[420px] xl:sticky xl:top-4 xl:h-[calc(100vh-8rem)]">
                  <ScheduleMap
                    jobs={dayJobs}
                    lanes={lanes}
                    techs={fleetTechs}
                    fallback={mapFallback}
                    day={day}
                    selectedJobId={selectedJobId}
                    onOpenJob={setSelectedJobId}
                  />
                </div>
              )}
            </div>

            <DragOverlay dropAnimation={null}>
              {activeJob ? (
                <div className="w-44">
                  <ScheduleJobCard job={activeJob} colour={activeLaneColour} timezone={timezone} onOpen={() => {}} overlay />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}

        <MoveConfirmDialog
          move={pendingMove}
          timezone={timezone}
          techName={techName}
          pending={move.isPending}
          onConfirm={confirmMove}
          onCancel={() => setPendingMove(null)}
        />

        <EstimateQuickViewSheet
          estimateId={selectedEstimateId}
          open={!!selectedEstimateId}
          onOpenChange={(open) => !open && setSelectedEstimateId(null)}
        />

        <JobQuickViewSheet
          jobId={selectedJobId}
          open={!!selectedJobId}
          onOpenChange={(open) => !open && setSelectedJobId(null)}
        />
      </div>
    </Layout>
  );
}

function LegendDot({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className={cn('h-2 w-2 rounded-full', className)} />
      {label}
    </span>
  );
}
